(() => {
  const repairDescriptionEncoding = (lead) => {
    if (!lead || lead.dataset.tisEncodingFixed === 'true') return;
    const replacements = [
      [[0xf0, 0x178, 0x17d, 0x2030], '&#x1F389;'],
      [[0xf0, 0x178, 0x2019, 0x2013], '&#x1F496;'],
      [[0xe2, 0x153, 0x201d], '&#x2714;'],
      [[0xe2, 0x20ac, 0x201c], '&#x2013;'],
      [[0xe2, 0x153, 0xa8], '&#x2728;'],
      [[0xe2, 0x2dc, 0x2018], '&#x2611;'],
      [[0xf0, 0x178, 0x17d, 0xa8], '&#x1F3A8;'],
      [[0xe2, 0x20ac, 0xa2], '&#x2022;'],
      [[0xf0, 0x178, 0x201c, 0x152], '&#x1F4CC;'],
      [[0xf0, 0x178, 0x201c, 0x160], '&#x1F4CA;'],
      [[0xf0, 0x178, 0x2019, 0x152], '&#x1F48C;'],
      [[0xf0, 0x178, 0x201d, 0x2019], '&#x1F512;'],
      [[0xe2, 0x20ac, 0x153], '&#x201C;'],
      [[0xe2, 0x20ac, 0x201d], '&#x201D;'],
      [[0xe2, 0x20ac, 0x2014], '&#x2014;'],
      [[0xc2, 0xa9], '&#xA9;'],
    ];
    let html = lead.innerHTML;
    replacements.forEach(([codePoints, replacement]) => {
      html = html.split(String.fromCodePoint(...codePoints)).join(replacement);
    });
    if (html !== lead.innerHTML) lead.innerHTML = html;
    lead.dataset.tisEncodingFixed = 'true';
  };

  const initializeGallery = (gallery) => {
    if (gallery.dataset.tisInitialized === 'true') return;
    gallery.dataset.tisInitialized = 'true';
    const thumbnails = [...gallery.querySelectorAll('[data-gallery-thumbnail]')];
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    const main = gallery.querySelector('.tis-product-gallery__main');
    const status = gallery.querySelector('[data-gallery-status]');
    const thumbnailStrip = gallery.querySelector('.tis-product-gallery__thumbs');
    let activeGalleryIndex = 0;

    const revealThumbnail = (thumbnail) => {
      if (!thumbnail || !thumbnailStrip) return;
      const stripBounds = thumbnailStrip.getBoundingClientRect();
      const thumbnailBounds = thumbnail.getBoundingClientRect();
      const leftEdge = stripBounds.left + thumbnailStrip.clientLeft;
      const topEdge = stripBounds.top + thumbnailStrip.clientTop;
      const rightEdge = leftEdge + thumbnailStrip.clientWidth;
      const bottomEdge = topEdge + thumbnailStrip.clientHeight;
      const left = thumbnailBounds.left < leftEdge ? thumbnailBounds.left - leftEdge : Math.max(0, thumbnailBounds.right - rightEdge);
      const top = thumbnailBounds.top < topEdge ? thumbnailBounds.top - topEdge : Math.max(0, thumbnailBounds.bottom - bottomEdge);
      // Scroll this rail only; scrollIntoView also moves the document when the
      // thumbnails fall below the viewport on short mobile/tablet screens.
      if (left || top) thumbnailStrip.scrollBy({ left, top, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    };

    const show = (index) => {
      if (!slides.length) return;
      activeGalleryIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => { const active = slideIndex === activeGalleryIndex; slide.hidden = !active; slide.classList.toggle('is-active', active); });
      thumbnails.forEach((thumbnail, thumbIndex) => { const active = thumbIndex === activeGalleryIndex; thumbnail.classList.toggle('is-active', active); thumbnail.setAttribute('aria-pressed', String(active)); });
      revealThumbnail(thumbnails[activeGalleryIndex]);
      if (status) status.textContent = `${activeGalleryIndex + 1} / ${slides.length}`;
    };
    thumbnails.forEach((thumbnail) => thumbnail.addEventListener('click', () => show(Number(thumbnail.dataset.index))));
    gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => show(activeGalleryIndex - 1));
    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => show(activeGalleryIndex + 1));
    main?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(activeGalleryIndex - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); show(activeGalleryIndex + 1); }
    });
    const syncHeight = () => gallery.style.setProperty('--gallery-main-height', `${Math.round(main?.getBoundingClientRect().height || 0)}px`);
    syncHeight();
    if ('ResizeObserver' in window && main) new ResizeObserver(syncHeight).observe(main);
  };

  const initializeRecommendations = (section) => {
    if (section.dataset.tisInitialized === 'true' || section.innerHTML.trim()) return;
    section.dataset.tisInitialized = 'true';
    const load = async () => {
      const response = await fetch(section.dataset.recommendationsUrl);
      if (!response.ok) return;
      const template = document.createElement('template');
      template.innerHTML = (await response.text()).trim();
      const replacement = template.content.querySelector('[data-product-recommendations]');
      if (replacement?.innerHTML.trim()) section.replaceWith(replacement);
    };
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) { observer.disconnect(); load(); } }, { rootMargin: '300px' });
      observer.observe(section);
    } else load();
  };

  const initializeDescriptionToggle = (root) => {
    root.querySelectorAll?.('[data-product-description]').forEach((wrapper) => {
      if (wrapper.dataset.tisInitialized === 'true') return;
      const content = wrapper.querySelector('[data-product-description-content]');
      const button = wrapper.querySelector('[data-product-description-toggle]');
      if (!content || !button) return;
      wrapper.dataset.tisInitialized = 'true';
      const label = button.querySelector('[data-description-toggle-label]') || button.querySelector('span');
      const collapsedLabel = button.dataset.collapsedLabel || label?.textContent || 'Learn more about this item';
      const expandedLabel = button.dataset.expandedLabel || 'Show less';
      const setExpanded = (expanded) => {
        content.classList.toggle('tis-product-lead--expanded', expanded);
        content.classList.toggle('tis-product-lead--collapsed', !expanded);
        button.setAttribute('aria-expanded', String(expanded));
        if (label) label.textContent = expanded ? expandedLabel : collapsedLabel;
      };
      setExpanded(button.getAttribute('aria-expanded') === 'true');
      button.addEventListener('click', () => setExpanded(button.getAttribute('aria-expanded') !== 'true'));
    });
  };

  const initializeProduct = (root = document) => {
    root.querySelectorAll?.('[data-product-gallery]').forEach(initializeGallery);
    root.querySelectorAll?.('[data-product-recommendations]').forEach(initializeRecommendations);
    initializeDescriptionToggle(root);
    root.querySelectorAll?.('.tis-product-lead, .tis-quick-view__description').forEach(repairDescriptionEncoding);
  };
  initializeProduct();
  document.addEventListener('shopify:section:load', (event) => initializeProduct(event.target));
})();
